import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, ChevronLeft, Search, Plus, User } from "lucide-react";

const STAGES = [
  { key: "en_attente", label: "Nouveau" },
  { key: "contacte", label: "Contacté" },
  { key: "rdv_fixe", label: "Visite planifiée" },
  { key: "devis_envoye", label: "Devis envoyé" },
  { key: "en_negociation", label: "En négociation" },
];

/**
 * Modal "Nouveau projet" 2 étapes utilisé depuis Pipeline (9bis-c).
 * - Étape 1 : sélection ou création de contact (inline).
 * - Étape 2 : titre / description / source / assignee → POST /api/projects.
 */
export function NewProjectFromPipeline({ open, onClose, onCreated }) {
  const [step, setStep] = useState(1);
  const [contact, setContact] = useState(null); // {id, first_name, last_name, email, phone}
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [inlineCreate, setInlineCreate] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
  });
  const [creatingContact, setCreatingContact] = useState(false);

  const [sources, setSources] = useState([]);
  const [members, setMembers] = useState([]);
  const [form, setForm] = useState({
    title: "",
    project_description: "",
    source_id: "",
    stage: "en_attente",
    assigned_to_user_id: "none",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Reset on open/close
  useEffect(() => {
    if (!open) {
      setStep(1);
      setContact(null);
      setQuery("");
      setResults([]);
      setInlineCreate({ first_name: "", last_name: "", email: "", phone: "" });
      setForm({
        title: "",
        project_description: "",
        source_id: sources[0]?.id || "",
        stage: "en_attente",
        assigned_to_user_id: "none",
      });
      setError(null);
    }
     
  }, [open]);

  // Load sources + members on mount
  useEffect(() => {
    if (!open) return;
    Promise.all([
      api.get("/lead-sources").catch(() => ({ data: [] })),
      api.get("/team/members").catch(() => ({ data: [] })),
    ]).then(([s, m]) => {
      const active = (s.data || []).filter((x) => x.active);
      setSources(active);
      // BLOC 19/10 — Filtrage is_active : désactivés exclus du dropdown d'assignation
      setMembers((m.data || []).filter((u) => u.is_active !== false));
      if (active.length && !form.source_id) {
        setForm((f) => ({ ...f, source_id: active[0].id }));
      }
    });
     
  }, [open]);

  // Debounced search
  useEffect(() => {
    if (!open || step !== 1) return;
    const q = query.trim();
    if (!q) {
      setResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get(`/contacts?limit=8&search=${encodeURIComponent(q)}`);
        setResults(data.items || []);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [query, open, step]);

  const displayName = (c) =>
    `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.email || c.phone || "(sans nom)";

  const selectContact = (c) => {
    setContact(c);
    if (!form.title) {
      setForm((f) => ({ ...f, title: `Projet ${displayName(c)}` }));
    }
    setStep(2);
  };

  const handleInlineCreate = async () => {
    const f = inlineCreate;
    if (!(f.first_name.trim() || f.last_name.trim() || f.email.trim())) {
      setError("Au moins un des champs prénom, nom ou email est requis.");
      return;
    }
    setCreatingContact(true);
    setError(null);
    try {
      const { data } = await api.post("/contacts", {
        first_name: f.first_name.trim(),
        last_name: f.last_name.trim(),
        email: f.email.trim() || null,
        phone: f.phone.trim(),
      });
      toast.success("Contact créé");
      selectContact(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setCreatingContact(false);
    }
  };

  const submit = async () => {
    if (!contact) {
      setError("Sélectionne d'abord un contact.");
      return;
    }
    if (!form.title.trim()) {
      setError("Un titre de projet est requis.");
      return;
    }
    if (!form.source_id) {
      setError("Sélectionne une source de lead.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        contact_id: contact.id,
        source_id: form.source_id,
        title: form.title.trim(),
        project_description: form.project_description.trim(),
        stage: form.stage,
        assigned_to_user_id: form.assigned_to_user_id === "none" ? null : form.assigned_to_user_id,
        tags: [],
      };
      const { data } = await api.post("/projects", payload);
      toast.success("Projet créé");
      onCreated?.(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const jumpToInline = () => {
    const q = query.trim();
    if (q.includes("@")) setInlineCreate((f) => ({ ...f, email: q }));
    else if (/^\+?\d[\d\s.-]{5,}/.test(q)) setInlineCreate((f) => ({ ...f, phone: q }));
    else if (q) {
      const parts = q.split(" ");
      setInlineCreate((f) => ({ ...f, first_name: parts[0] || "", last_name: parts.slice(1).join(" ") }));
    }
    setStep(0); // 0 = inline create sub-step
  };

  const renderStep1 = () => (
    <div className="space-y-3 py-2">
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Rechercher un contact par nom, email ou téléphone…"
          className="border-slate-700 bg-slate-950/50 pl-9 text-slate-100"
          autoFocus
          data-testid="npf-search-input"
        />
      </div>

      {searching ? (
        <div className="flex justify-center py-6">
          <Loader2 className="h-5 w-5 animate-spin text-slate-500" />
        </div>
      ) : results.length > 0 ? (
        <div className="max-h-60 space-y-1 overflow-y-auto" data-testid="npf-results">
          {results.map((c) => (
            <button
              key={c.id}
              onClick={() => selectContact(c)}
              className="flex w-full items-center gap-3 rounded border border-slate-800 bg-slate-950/40 p-2.5 text-left transition hover:border-[#C4553B]/60 hover:bg-slate-900/60"
              data-testid={`npf-result-${c.id}`}
            >
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#C4553B]/20 text-sm font-medium text-[#E07A62]">
                {(c.first_name?.[0] || "") + (c.last_name?.[0] || "") || <User className="h-4 w-4" />}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-slate-100">{displayName(c)}</p>
                <p className="truncate text-xs text-slate-500">{c.email || c.phone || "—"}</p>
              </div>
              {(c.projects_count || 0) > 0 && (
                <Badge variant="outline" className="border-slate-700 bg-slate-800 text-[10px] text-slate-400">
                  {c.projects_count} projet{c.projects_count > 1 ? "s" : ""}
                </Badge>
              )}
            </button>
          ))}
        </div>
      ) : query.trim() ? (
        <div className="rounded border border-dashed border-slate-800 p-4 text-center text-sm text-slate-500">
          Aucun contact ne correspond à "{query}".
        </div>
      ) : (
        <div className="rounded border border-dashed border-slate-800 p-4 text-center text-xs text-slate-500">
          Tapez pour rechercher un contact existant.
        </div>
      )}

      <Button
        variant="outline"
        onClick={jumpToInline}
        className="w-full border-slate-700 bg-slate-800/50 text-slate-200"
        data-testid="npf-jump-create"
      >
        <Plus className="mr-2 h-4 w-4" /> Créer un nouveau contact
      </Button>
    </div>
  );

  const renderInline = () => (
    <div className="space-y-3 py-2">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-slate-400">Prénom</Label>
          <Input
            value={inlineCreate.first_name}
            onChange={(e) => setInlineCreate({ ...inlineCreate, first_name: e.target.value })}
            className="border-slate-700 bg-slate-950/50 text-slate-100"
            data-testid="npf-inline-first"
          />
        </div>
        <div>
          <Label className="text-xs text-slate-400">Nom</Label>
          <Input
            value={inlineCreate.last_name}
            onChange={(e) => setInlineCreate({ ...inlineCreate, last_name: e.target.value })}
            className="border-slate-700 bg-slate-950/50 text-slate-100"
            data-testid="npf-inline-last"
          />
        </div>
      </div>
      <div>
        <Label className="text-xs text-slate-400">Email</Label>
        <Input
          type="email"
          value={inlineCreate.email}
          onChange={(e) => setInlineCreate({ ...inlineCreate, email: e.target.value })}
          className="border-slate-700 bg-slate-950/50 text-slate-100"
          data-testid="npf-inline-email"
        />
      </div>
      <div>
        <Label className="text-xs text-slate-400">Téléphone</Label>
        <Input
          value={inlineCreate.phone}
          onChange={(e) => setInlineCreate({ ...inlineCreate, phone: e.target.value })}
          placeholder="0470 12 34 56"
          className="border-slate-700 bg-slate-950/50 text-slate-100"
          data-testid="npf-inline-phone"
        />
      </div>
      {error && (
        <div className="rounded border border-red-800/50 bg-red-900/20 p-2 text-xs text-red-200" data-testid="npf-error">
          {error}
        </div>
      )}
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-3 py-2">
      <div className="rounded border border-slate-800 bg-slate-950/40 p-2 text-xs text-slate-300">
        Contact : <span className="font-medium text-slate-100">{displayName(contact)}</span>
        {contact?.email && <span className="ml-2 text-slate-500">· {contact.email}</span>}
      </div>
      <div>
        <Label htmlFor="npf-title" className="text-xs text-slate-400">Titre du projet *</Label>
        <Input
          id="npf-title"
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          className="border-slate-700 bg-slate-950/50 text-slate-100"
          data-testid="npf-title"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-slate-400">Source</Label>
          <Select key={`src-${form.source_id || "empty"}`} value={form.source_id} onValueChange={(v) => setForm({ ...form, source_id: v })}>
            <SelectTrigger className="border-slate-700 bg-slate-950/50" data-testid="npf-source">
              <SelectValue placeholder="Sélectionner…" />
            </SelectTrigger>
            <SelectContent className="border-slate-700 bg-slate-900 text-slate-100">
              {sources.map((s) => (
                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-slate-400">Statut initial</Label>
          <Select value={form.stage} onValueChange={(v) => setForm({ ...form, stage: v })}>
            <SelectTrigger className="border-slate-700 bg-slate-950/50" data-testid="npf-stage">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="border-slate-700 bg-slate-900 text-slate-100">
              {STAGES.map((s) => (
                <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label className="text-xs text-slate-400">Commercial du projet</Label>
        <Select value={form.assigned_to_user_id} onValueChange={(v) => setForm({ ...form, assigned_to_user_id: v })}>
          <SelectTrigger className="border-slate-700 bg-slate-950/50" data-testid="npf-assignee">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="border-slate-700 bg-slate-900 text-slate-100">
            <SelectItem value="none">Non assigné</SelectItem>
            {members.map((m) => (
              <SelectItem key={m.id} value={m.id}>{m.first_name} {m.last_name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs text-slate-400">Description</Label>
        <Textarea
          value={form.project_description}
          onChange={(e) => setForm({ ...form, project_description: e.target.value })}
          rows={3}
          placeholder="Ex : Installation photovoltaïque 12 panneaux…"
          className="border-slate-700 bg-slate-950/50 text-slate-100"
          data-testid="npf-description"
        />
      </div>
      {error && (
        <div className="rounded border border-red-800/50 bg-red-900/20 p-2 text-xs text-red-200" data-testid="npf-error">
          {error}
        </div>
      )}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !saving && !creatingContact && onClose?.()}>
      <DialogContent
        className="max-w-lg border-slate-800 bg-[#1E1A17] text-slate-100"
        data-testid="new-project-from-pipeline-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-slate-100">
            {step === 2 ? "Nouveau projet — Détails" : step === 0 ? "Nouveau projet — Créer le contact" : "Nouveau projet — Contact"}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {step === 2
              ? "Renseignez le titre du projet et sa source."
              : step === 0
                ? "Créez un contact minimal — vous pourrez le compléter après."
                : "Étape 1 sur 2 : choisir le contact du projet."}
          </DialogDescription>
        </DialogHeader>

        {step === 1 && renderStep1()}
        {step === 0 && renderInline()}
        {step === 2 && renderStep2()}

        <DialogFooter>
          {step === 0 && (
            <Button
              variant="outline"
              onClick={() => setStep(1)}
              disabled={creatingContact}
              className="border-slate-700 bg-slate-800/50 text-slate-200"
              data-testid="npf-back-to-search"
            >
              <ChevronLeft className="mr-1 h-3 w-3" /> Retour recherche
            </Button>
          )}
          {step === 2 && (
            <Button
              variant="outline"
              onClick={() => {
                setContact(null);
                setStep(1);
              }}
              disabled={saving}
              className="border-slate-700 bg-slate-800/50 text-slate-200"
              data-testid="npf-back-to-contact"
            >
              <ChevronLeft className="mr-1 h-3 w-3" /> Changer de contact
            </Button>
          )}
          <Button
            variant="outline"
            onClick={onClose}
            disabled={saving || creatingContact}
            className="border-slate-700 bg-slate-800/50 text-slate-200"
            data-testid="npf-cancel"
          >
            Annuler
          </Button>
          {step === 0 && (
            <Button
              onClick={handleInlineCreate}
              disabled={creatingContact}
              className="bg-[#C4553B] text-white hover:bg-[#B44A32]"
              data-testid="npf-inline-create"
            >
              {creatingContact && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Créer et continuer
            </Button>
          )}
          {step === 2 && (
            <Button
              onClick={submit}
              disabled={saving || !form.source_id || !form.title.trim()}
              className="bg-[#C4553B] text-white hover:bg-[#B44A32]"
              data-testid="npf-submit"
            >
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Créer le projet
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default NewProjectFromPipeline;
